import React from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { LIFECYCLE_OPTIONS } from '../assets/informationSystemConstants'
import DataMapDataFlowsSection from '../assets/DataMapDataFlowsSection'
import DataMapSummaryRow from '../assets/DataMapSummaryRow'
import DataMapDetailPanel, { DataMapDetailEmpty } from '../assets/DataMapDetailPanel'
import type { DataMapRow } from '../assets/dataMapRowParts'
import type { InformationSystemSummary } from '../../services/informationSystemsApi'
import {
  fetchDataMapExecutiveSummary,
  type DataMapExecutiveSummary,
  type InformationSystemExecutiveSummary,
} from '../../services/enterpriseOperationalUxApi'
import DataMapOperationalFlowCard from './DataMapOperationalFlowCard'
import DataMapExecutiveContextPanel, { type DataMapSelection } from './DataMapExecutiveContextPanel'
import DataMapTopologyMiniGraph from './DataMapTopologyMiniGraph'
import DataMapOperationalIndicators from './DataMapOperationalIndicators'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'

type Props = {
  systems: InformationSystemSummary[]
  dataMap: DataMapRow[]
  catalog: string[]
  loading: boolean
  error: string | null
  searchQuery: string
  setSearchQuery: (v: string) => void
  filterLifecycle: string
  setFilterLifecycle: (v: string) => void
  filterCategory: string
  setFilterCategory: (v: string) => void
  filterClassification: string
  setFilterClassification: (v: string) => void
  filterInformationSystem: string
  setFilterInformationSystem: (v: string) => void
  onRefresh: () => void
  submitting: boolean
  onAssign: (payload: {
    category: string
    classification: string
    processingPurpose: string
    storageLocation: string
    retentionPeriod: string
    crossBorderTransfer: boolean
    notes: string
  }) => Promise<void>
  onRemove: (dataCategoryId: string) => Promise<void>
}

export default function DataMapExecutiveWorkspace({
  systems,
  dataMap,
  catalog,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  filterLifecycle,
  setFilterLifecycle,
  filterCategory,
  setFilterCategory,
  filterClassification,
  setFilterClassification,
  filterInformationSystem,
  setFilterInformationSystem,
  onRefresh,
  submitting,
  onAssign,
  onRemove,
}: Props) {
  const { t } = useI18n()
  const [summary, setSummary] = React.useState<DataMapExecutiveSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = React.useState(true)
  const [flowsOpen, setFlowsOpen] = React.useState(true)
  const [graphOpen, setGraphOpen] = React.useState(true)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [selection, setSelection] = React.useState<DataMapSelection>(null)

  const loadSummary = React.useCallback(async () => {
    setSummaryLoading(true)
    try {
      const data = await fetchDataMapExecutiveSummary()
      setSummary(data?.enterpriseUxEnabled !== false ? data : null)
    } catch {
      setSummary(null)
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadSummary()
  }, [loadSummary, dataMap.length])

  const executiveBySystemId = React.useMemo(() => {
    const map: Record<string, InformationSystemExecutiveSummary> = {}
    for (const s of summary?.systems || []) {
      map[s.informationSystemId] = s
    }
    return map
  }, [summary?.systems])

  const filteredOperationalFlows = React.useMemo(() => {
    const flows = summary?.flows || []
    if (!filterInformationSystem) return flows
    return flows.filter(
      (f) =>
        f.sourceSystemId === filterInformationSystem || f.targetSystemId === filterInformationSystem,
    )
  }, [summary?.flows, filterInformationSystem])

  const filteredRows = React.useMemo(() => {
    let rows = dataMap
    const q = searchQuery.trim().toLowerCase()
    if (q) {
      rows = rows.filter(
        (row) =>
          row.informationSystemName.toLowerCase().includes(q) ||
          (row.informationSystemCode || '').toLowerCase().includes(q),
      )
    }
    if (filterInformationSystem) {
      const relatedIds = new Set<string>([filterInformationSystem])
      for (const f of filteredOperationalFlows) {
        relatedIds.add(f.sourceSystemId)
        relatedIds.add(f.targetSystemId)
      }
      rows = rows.filter((row) => relatedIds.has(row.informationSystemId))
    }
    return rows
  }, [dataMap, searchQuery, filterInformationSystem, filteredOperationalFlows])

  const selectedRow = React.useMemo(
    () => (selectedId ? dataMap.find((r) => r.informationSystemId === selectedId) : null),
    [dataMap, selectedId],
  )

  const posture = summary?.postureStrip

  const sortedSystems = React.useMemo(
    () => [...systems].sort((a, b) => a.name.localeCompare(b.name)),
    [systems],
  )

  const handleSelectSystem = (row: DataMapRow) => {
    setSelectedId(row.informationSystemId)
    setSelection({ kind: 'system', systemId: row.informationSystemId, row })
  }

  const handleSelectFlow = (flowId: string) => {
    const flow = filteredOperationalFlows.find((f) => f.id === flowId)
    if (flow) setSelection({ kind: 'flow', flow })
  }

  return (
    <div className={styles.dataMapWorkspace} data-testid="data-map-executive-workspace">
      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>
          {t('enterpriseOperationalUx.dataMap.layerPosture', 'Operational topology posture')}
        </p>
        <div className={entStyles.posturePanel}>
          <h2 className={entStyles.postureTitle}>
            {t('enterpriseOperationalUx.dataMap.operationalTopology', 'Operational Compliance Topology')}
          </h2>
          <p className={entStyles.postureSubtitle}>
            {t(
              'enterpriseOperationalUx.dataMap.workspaceHint',
              'Which systems process critical data, which standards apply, and where operational risk propagates across flows.',
            )}
          </p>
          {summaryLoading ? <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p> : null}
          {posture ? (
            <div className={entStyles.postureGrid}>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>{t('enterpriseOperationalUx.scope', 'Scope')}</p>
                <p className={entStyles.postureMetricValue}>{posture.systemsInScope}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.dataMap.criticalDataSystems', 'Critical data systems')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.criticalDataSystems}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>{t('enterpriseOperationalUx.degradedShort', 'degraded')}</p>
                <p className={entStyles.postureMetricValue}>{posture.degradedSystems}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.dataMap.highestPressure', 'Highest pressure')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.highestPressureSystems}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>{t('enterpriseOperationalUx.staleEvidence', 'Stale evidence')}</p>
                <p className={entStyles.postureMetricValue}>{posture.staleEvidenceSystems}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.dataMap.runtimeInstability', 'Runtime instability')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.runtimeInstabilityCount}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.dataMap.crossStandardBlast', 'Cross-standard blast')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.crossStandardBlastRadiusCount}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('enterpriseOperationalUx.dataMap.degradedFlows', 'Degraded flows')}
                </p>
                <p className={entStyles.postureMetricValue}>{posture.degradedFlows}</p>
              </div>
            </div>
          ) : null}
          {summary?.explanations?.length ? (
            <p className={entStyles.postureStory}>{summary.explanations.join(' ')}</p>
          ) : null}
        </div>
      </section>

      {(summary?.topologyEvents?.length || summary?.blastRadiusImpacts?.length) ? (
        <section className={styles.topologySignalsRow}>
          {summary.topologyEvents?.length ? (
            <div className={styles.signalBlock}>
              <h3 className={styles.signalTitle}>{t('enterpriseOperationalUx.layerTimeline', 'Runtime operational timeline')}</h3>
              <ul className={styles.signalList}>
                {summary.topologyEvents.slice(0, 6).map((ev) => (
                  <li key={ev.id}>
                    <button
                      type="button"
                      className={styles.signalBtn}
                      onClick={() => setSelection({ kind: 'event', event: ev })}
                    >
                      <span className={styles.runtimeDot} aria-hidden />
                      {ev.eventType} — {ev.summary}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className={styles.widgetEmpty}>
              {t('enterpriseOperationalUx.dataMap.noTopologyEvents', 'No operational topology events yet')}
            </p>
          )}
          {summary.blastRadiusImpacts?.length ? (
            <div className={styles.signalBlock}>
              <h3 className={styles.signalTitle}>{t('enterpriseOperationalUx.layerBlast', 'Shared control blast radius')}</h3>
              <ul className={styles.signalList}>
                {summary.blastRadiusImpacts.slice(0, 5).map((imp) => (
                  <li key={imp.controlCode}>
                    <button
                      type="button"
                      className={styles.signalBtn}
                      onClick={() => setSelection({ kind: 'topologyIssue', impact: imp })}
                    >
                      {imp.controlCode} — {imp.summary}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className={styles.widgetEmpty}>
              {t('enterpriseOperationalUx.dataMap.noDegradedTopology', 'No degraded topology detected')}
            </p>
          )}
        </section>
      ) : null}

      <section className={styles.auxiliarySection}>
        <div className={styles.auxiliarySectionHeader}>
          <h2 className={styles.auxiliarySectionTitle}>
            {t('assets.dataMap.flowsSectionTitle', 'Data flows & export')}
          </h2>
          <button type="button" className={styles.collapseBtn} onClick={() => setFlowsOpen((v) => !v)} aria-expanded={flowsOpen}>
            {flowsOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
          </button>
        </div>
        {flowsOpen ? (
          <DataMapDataFlowsSection
            systems={systems}
            catalog={catalog}
            onChanged={onRefresh}
            filterSystemId={filterInformationSystem || undefined}
          />
        ) : null}
      </section>

      <div className={styles.dmToolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.toolbarPrimaryRow}>
            <input
              type="search"
              className={styles.searchField}
              placeholder={t('assets.dataMap.searchPlaceholder', 'Search systems…')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.lifecycle.status', 'Lifecycle')}</span>
              <select className={styles.selectField} value={filterLifecycle} onChange={(e) => setFilterLifecycle(e.target.value)}>
                <option value="">{t('assets.lifecycle.allStatuses', 'All statuses')}</option>
                {LIFECYCLE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.lifecycle.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.dataMap.category', 'Data category')}</span>
              <select className={styles.selectField} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="">{t('assets.dataMap.allCategories', 'All categories')}</option>
                {catalog.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.dataMap.system', 'Information system')}</span>
              <select
                className={styles.selectField}
                value={filterInformationSystem}
                onChange={(e) => setFilterInformationSystem(e.target.value)}
              >
                <option value="">{t('assets.dataMap.allSystems', 'All systems')}</option>
                {sortedSystems.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.dataMap.classification', 'Classification')}</span>
              <input
                className={styles.textField}
                style={{ maxWidth: '160px', width: '160px' }}
                value={filterClassification}
                onChange={(e) => setFilterClassification(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className={styles.toolbarRight}>
          <button type="button" className={styles.refreshBtn} onClick={() => { onRefresh(); void loadSummary() }}>
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {error ? (
        <p className={styles.errorBanner}>
          {error}
          <Button type="button" size="small" color="outline" onClick={onRefresh}>
            {t('common.retry', 'Retry')}
          </Button>
        </p>
      ) : null}

      <section className={styles.topologyWorkspace}>
        <div className={styles.topologyMain}>
          <h3 className={styles.topologySectionTitle}>
            {t('enterpriseOperationalUx.dataMap.flowTopology', 'Operational data flows')}
          </h3>
          {loading || summaryLoading ? (
            <p className={styles.loadingText}>{t('common.loading')}</p>
          ) : filteredOperationalFlows.length === 0 ? (
            <p className={styles.widgetEmpty}>
              {filterInformationSystem
                ? t('assets.dataMap.emptyFlowsForSystem', 'No data flows for this information system as source or target.')
                : t('enterpriseOperationalUx.dataMap.noCriticalFlows', 'No systems with critical data flows')}
            </p>
          ) : (
            <div className={styles.topologyFlowGrid}>
              {filteredOperationalFlows.map((flow) => (
                <DataMapOperationalFlowCard
                  key={flow.id}
                  flow={flow}
                  selected={selection?.kind === 'flow' && selection.flow.id === flow.id}
                  onSelect={() => handleSelectFlow(flow.id)}
                />
              ))}
            </div>
          )}

          <div className={styles.auxiliarySectionHeader} style={{ marginTop: '1rem' }}>
            <h3 className={styles.topologySectionTitle}>
              {t('enterpriseOperationalUx.dataMap.systemCategories', 'Systems & critical data')}
            </h3>
          </div>
          {filteredRows.length === 0 ? (
            <div className={styles.emptyCard}>
              <p className={styles.emptyHint}>{t('assets.dataMap.emptyFiltered', 'No systems match the current filters.')}</p>
            </div>
          ) : (
            <div className={styles.dmSystemsList}>
              {filteredRows.map((row) => {
                const ex = executiveBySystemId[row.informationSystemId]
                return (
                  <div key={row.informationSystemId} className={styles.topologySystemWrap}>
                    <DataMapSummaryRow
                      row={row}
                      selected={selectedId === row.informationSystemId}
                      onSelect={() => handleSelectSystem(row)}
                    />
                    {ex?.hasEnoughHistory ? (
                      <DataMapOperationalIndicators
                        compact
                        pressure={ex.pressure}
                        degraded={(ex.degradedControlCount || 0) > 0}
                        staleEvidence={(ex.staleEvidenceCount || 0) > 0}
                        criticalData={(row.dataCategories || []).some((d) =>
                          /payment|personal|pci|pii|banking/i.test(d.category),
                        )}
                        runtimeWarning={Boolean(ex.latestRuntimeEvent)}
                        drift={ex.readinessTrajectory === 'DECLINING'}
                      />
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}

          <section className={styles.auxiliarySection} style={{ marginTop: '1rem' }}>
            <div className={styles.auxiliarySectionHeader}>
              <h3 className={styles.auxiliarySectionTitle}>
                {t('enterpriseOperationalUx.dataMap.topologyGraph', 'Topology graph')}
              </h3>
              <button type="button" className={styles.collapseBtn} onClick={() => setGraphOpen((v) => !v)}>
                {graphOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
              </button>
            </div>
            {graphOpen ? (
              <DataMapTopologyMiniGraph flows={filteredOperationalFlows} executiveBySystemId={executiveBySystemId} />
            ) : null}
          </section>
        </div>

        <aside className={styles.topologyAside}>
          <DataMapExecutiveContextPanel
            selection={selection}
            executiveBySystemId={executiveBySystemId}
            onClose={() => setSelection(null)}
          />
          {selectedRow ? (
            <div className={styles.detailPanelStack}>
              <DataMapDetailPanel
                row={selectedRow}
                catalog={catalog}
                submitting={submitting}
                onClose={() => {
                  setSelectedId(null)
                  if (selection?.kind === 'system') setSelection(null)
                }}
                onRemove={onRemove}
                onAssign={onAssign}
              />
            </div>
          ) : (
            <DataMapDetailEmpty />
          )}
        </aside>
      </section>
    </div>
  )
}
